import React, { useEffect, useState } from 'react';

import { useDarkMode } from '@/hooks/useDarkMode';
import constants from '@/theme/exportedVariables.module.scss';
import { Container } from '@nivo/core';
import {
	LineSvgProps,
	ResponsiveLine,
	Serie,
	SliceTooltip,
	SliceTooltipProps,
} from '@nivo/line';
import { mergeDeepLeft } from 'ramda';
import { useTimeoutFn } from 'react-use';

import { getFeedbackStepDetails } from '@/components/shared/FeedbackScale';

import styles from './LineChart.module.scss';

export type LineChartData = Serie[];

export type LineChartProps = {
	data: LineChartData;
	maxValue?: number;
	wrapperClassName?: string;
	animationDelay?: number;
	colors?: string[];
	noAxisBottom?: boolean;
	min?: number;
	emojiYAxis?: boolean;
	highlightedSerieId?: string | null;
} & Pick<
	LineSvgProps,
	| 'margin'
	| 'axisBottom'
	| 'curve'
	| 'enablePoints'
	| 'axisLeft'
	| 'isInteractive'
	| 'colors'
	| 'lineWidth'
	| 'defs'
	| 'enableArea'
	| 'fill'
	| 'theme'
	| 'animate'
	| 'layers'
	| 'enableGridX'
	| 'enableGridY'
	| 'gridXValues'
	| 'xScale'
	| 'areaBaselineValue'
>;

export const LineChart: React.FC<LineChartProps> = ({
	data,
	maxValue,
	wrapperClassName,
	animationDelay,
	curve,
	margin,
	axisBottom,
	noAxisBottom,
	theme,
	colors,
	min,
	emojiYAxis,
	highlightedSerieId,
	...otherLineChartProps
}) => {
	const { isDarkModeOn } = useDarkMode();
	const defaultColors = colors || ['#beb1d8', '#c7ff4e', '#F9AEA1'];
	const resolvedColors = highlightedSerieId
		? (serie: any) => {
			const idx = data.findIndex((d) => d.id === serie.id);
			const base = (defaultColors as string[])[idx] ?? '#beb1d8';
			return serie.id === highlightedSerieId ? base : base + '40';
		  }
		: defaultColors;
	// const [animationComplete, setAnimationComplete] = useState(false);
	// // Needed for the initial animation
	// // It will assign the data with y: 0 for all data fields
	// // and when the useTimeoutFn runs it will set it to the initial values
	// const [data, setData] = useState<LineChartData>(
	// 	originalData.map((idObj) => ({
	// 		...idObj,
	// 		data: idObj.data.map((dataObj) => ({ ...dataObj, y: 0 })),
	// 	})),
	// );
	// // Second part of the animation logic
	// useTimeoutFn(() => {
	// 	setData(originalData);
	// 	setAnimationComplete(true);
	// }, animationDelay || 200);

	// useEffect(() => {
	// 	if (animationComplete) {
	// 		setData(originalData);
	// 	}
	// }, [originalData, animationComplete]);

	return (
		<div className={wrapperClassName}>
			<ResponsiveLine
				data={data}
				areaOpacity={1}
				animate
				curve={curve || 'monotoneX'}
				enableSlices="x"
				colors={resolvedColors as any}
				theme={mergeDeepLeft(theme || {}, {
					text: {
						fill: isDarkModeOn ? '#ffffff' : '#000000',
					},
					grid: {
						line: {
							stroke: isDarkModeOn ? '#313a4e' : '#cfdcf1',
							strokeDasharray: 3,
						},
					},
					axis: {
						ticks: {
							line: {
								stroke: isDarkModeOn ? '#313a4e' : '#cfdcf1',
							},
						},
					},
					crosshair: {
						line: {
							stroke: isDarkModeOn ? '#55658c' : '#abbad7',
							strokeDasharray: '0',
						},
					},
				})}
				margin={{
					bottom: margin?.bottom ?? 35,
					left: margin?.left ?? 35,
					right: margin?.right ?? 25,
					top: margin?.top ?? 20,
				}}
				yScale={{
					type: 'linear',
					min: min ?? 1,
					max: maxValue || 5,
					stacked: false,
				}}
				yFormat=" >.1~f"
				axisTop={null}
				axisRight={null}
				axisBottom={
					noAxisBottom
						? null
						: {
								tickSize: axisBottom?.tickSize || 5,
								tickPadding: axisBottom?.tickPadding || 10,
								tickRotation: 0,
								renderTick: axisBottom?.renderTick,
								format: axisBottom?.format,
								tickValues: axisBottom?.tickValues,
						  }
				}
				axisLeft={{
					tickSize: 0,
					tickPadding: emojiYAxis ? 12 : 15,
					ticksPosition: 'before',
					tickRotation: 0,
					...(emojiYAxis
						? {
								renderTick: ({ x, y, value, textX, textY }: any) =>
									Number.isInteger(value) ? (
										<g transform={`translate(${x},${y})`}>
											<text
												x={textX}
												y={textY}
												textAnchor="end"
												dominantBaseline="central"
												style={{ fontSize: 16 }}
											>
												{getFeedbackStepDetails(value).emoji}
											</text>
										</g>
									) : (
										<g />
									),
						  }
						: { format: (e: any) => (Number.isInteger(e) ? e : '') }),
				}}
				pointSize={10}
				// pointColor={{ theme: 'background' }}
				pointBorderWidth={2}
				pointBorderColor={isDarkModeOn ? '#1d222c' : '#ffffff'}
				// pointLabelYOffset={-12}
				sliceTooltip={Tooltip}
				useMesh={true}
				enableGridX={false}
				gridYValues={[1, 2, 3, 4, 5]}
				{...otherLineChartProps}
			/>
		</div>
	);
};

const Tooltip: React.FC<SliceTooltipProps> = ({ axis, slice }) => {
	return (
		<div className={styles['tooltip']}>
			{['personal', 'team', 'congregation'].map((pointKey) => {
				const point = slice.points.find((p) => p.serieId === pointKey);
				if (!point) {
					return null;
				}
				return (
					<div className={styles['score-row']}>
						<div
							className={styles['color-key']}
							style={{ backgroundColor: point.serieColor }}
						/>
						<div className={styles['name']}>{point.serieId}</div>
						<div className={styles['score']}>
							{point.data.yFormatted}
						</div>
					</div>
				);
			})}
		</div>
	);
};
